import { useAuthStore } from "../auth/auth.store";
import { useUserStore } from "../user/user.store";

export interface ParentAuthService {
  getToken: () => Promise<string>;
  renewToken: () => Promise<void>;
  getTokenClaims: () => Record<string, unknown>;
  login: (route?: unknown) => Promise<void>;
  logout: () => Promise<void>;
  processCallback: () => Promise<unknown>;
}

/**
 * The `parentAuthService` mount arg of the legacy AppMount contract. Mirrors
 * MerchantApp's src/components/micro-front-ends/utils/parentAuthService.js:
 * getTokenClaims() is built from the user-management user, not the OIDC
 * profile (the ShipBob.OneId.User access token is a reference token and
 * carries no claims client-side).
 */
export function createParentAuthService(): ParentAuthService {
  const authStore = useAuthStore();
  const userStore = useUserStore();

  return {
    async getToken() {
      return (await authStore.getAccessToken()) ?? "";
    },

    async renewToken() {
      await authStore.refreshToken();
    },

    getTokenClaims() {
      const user = userStore.loggedInUser;
      const impersonated = userStore.impersonatedUser;
      const claim = userStore.oneIdClaim;
      const profile = (authStore.userProfile as Record<string, unknown> | null) ?? {};

      const oneid = claim
        ? JSON.stringify({
            fcid: null,
            lorg: claim.Lorg ?? null,
            lorg_type: claim.lorg_type ?? null,
            morg: claim.Morg ?? null,
            perm: claim.Perm ?? null,
            perm_names: claim.perm_names ?? null,
            org_atts: claim.org_atts ?? null,
            features: claim.features ?? null,
          })
        : null;

      return {
        shipbob_user_id: user?.UserId ?? profile.shipbob_user_id ?? null,
        shipbob_impersonating_user_id:
          impersonated?.UserId ?? profile.shipbob_impersonating_user_id ?? null,
        shipbob_role:
          typeof profile.shipbob_role === "string" || typeof profile.shipbob_role === "number"
            ? String(profile.shipbob_role)
            : null,
        oneid,
        ...user,
      };
    },

    async login() {
      await authStore.login();
    },

    async logout() {
      await authStore.logout();
    },

    async processCallback() {
      await authStore.handleCallback();
      return null;
    },
  };
}
